import { buildMusicalChart } from './charts.js';
import bundledCatalog from './catalog.json';

/**
 * AI-generated catalog (MiniMax Music 2.6 via fal.ai).
 * The bundled src/data/catalog.json is only the offline fallback: the live
 * list comes from GET /api/catalog at startup, so songs generated daily on
 * the VPS appear without a client rebuild. Energy analyses are fetched at
 * runtime from /audio/analysis/<chartId>.json (same reason).
 * offset: game uses songTime = mediaTime + offset, first beat at -offset.
 */

/** Mutated in place by loadCatalog() so existing imports stay live. */
export const SONGS = bundledCatalog.map((entry) => ({ ...entry }));

let dailySong = null; // today's generated song (server-provided), if any

/**
 * Refresh the song list from the server. Safe offline: keeps the bundled
 * fallback. Returns { songs, daily }.
 */
export async function loadCatalog() {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    const res = await fetch('/api/catalog', { signal: controller.signal });
    clearTimeout(timer);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data?.songs) && data.songs.length) {
        SONGS.length = 0;
        SONGS.push(...data.songs.map((entry) => ({ ...entry })));
      }
      dailySong = data?.daily && typeof data.daily === 'object' ? { ...data.daily } : null;
    }
  } catch {
    /* offline: bundled catalog stays */
  }
  return { songs: SONGS, daily: dailySong };
}

/** Today's server-generated song (joins the Play catalog tomorrow), or null. */
export function getDailySong() {
  return dailySong;
}

const chartCache = new Map();
const analysisCache = new Map();

/** Energy analysis fetched once per chart (promise cached, null on failure). */
function loadAnalysis(chartId) {
  if (!analysisCache.has(chartId)) {
    analysisCache.set(
      chartId,
      fetch(`/audio/analysis/${encodeURIComponent(chartId)}.json`)
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null)
    );
  }
  return analysisCache.get(chartId);
}

/** Chart for a song at a difficulty, built on demand and cached. Async: fetches the analysis. */
export async function getChart(song, difficulty = 'normal') {
  const chartId = song.chartId ?? song.id;
  const key = `${chartId}|${difficulty}`;
  if (!chartCache.has(key)) {
    const analysis = await loadAnalysis(chartId);
    if (!chartCache.has(key)) {
      chartCache.set(key, buildMusicalChart(song.bpm, song.duration, analysis, difficulty));
    }
  }
  return chartCache.get(key);
}

export function getSongById(id) {
  return SONGS.find((s) => s.id === id) ?? SONGS[0];
}
